import { errorToast } from "@/components/ui/toast.tsx";
import { cn } from "@/lib/utils.ts";
import { escapeUnparseableAngleBrackets } from "@/components/markdown/utils.ts";
import {
  BoldItalicUnderlineToggles,
  type CodeBlockEditorDescriptor,
  codeBlockPlugin,
  CreateLink,
  headingsPlugin,
  imagePlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  type MDXEditorMethods,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Markdown } from "../markdown/Markdown.tsx";
import { ArtifactImageDialog } from "./ArtifactImageDialog.tsx";
import type { ImageSrcOverrides } from "@/shared/messageImageSrc.ts";
import { resolveEditorImageSrc } from "./editorImageSrc.ts";

// MDXEditor portals its popups (the link dialog, etc.) into a
// `.mdxeditor-popup-container` appended to `document.body` — outside `editorRef`,
// so they can escape the editor's clipping/overflow ancestors. Focus moving into
// that popup is logically still "in the editor": it must not hide the toolbar or
// trigger `saveOnBlur`, which would otherwise persist the pre-link content and drop
// the link the user is inserting (BIG-5406).
const focusMovedToEditorPopup = (related: EventTarget | null): boolean =>
  related instanceof Element && related.closest(".mdxeditor-popup-container") !== null;

/**
 * Renders a fenced code block as static markup.
 *
 * `codeBlockPlugin` registers the *node* but no editor for it. With no
 * descriptor matching a block, the importer throws on the first ``` fence and
 * Lexical swallows the error mid-update — so the document silently truncates at
 * that point, or comes up blank when the fence is near the top (BIG-5884).
 *
 * A `<pre>` is all a body needs to display. Editing code inline would mean
 * pulling in `codeMirrorPlugin` and its language bundles, which is a lot of
 * weight for a surface that is mostly read.
 */
const staticCodeBlockDescriptor: CodeBlockEditorDescriptor = {
  priority: 0,
  match: () => true,
  Editor: ({ code, language }) => (
    <pre className={language ? `language-${language}` : undefined}>
      <code>{code}</code>
    </pre>
  ),
};

interface ArtifactEditorProps {
  content: string;
  readOnly?: boolean;
  handleSave: (markdown: string) => Promise<void>;
  showDebug?: boolean;
  hideToolbar?: boolean;
  className?: string;
  contentEditableClassName?: string;
  onChange?: (markdown: string) => void;
  /** A shared reader's paths for the file images the body shows; see `markdownComponent`. */
  imageSrcs?: ImageSrcOverrides;
}

export const ArtifactEditor = ({
  content,
  readOnly,
  handleSave,
  showDebug,
  hideToolbar = false,
  className,
  contentEditableClassName,
  onChange,
  imageSrcs,
}: ArtifactEditorProps) => {
  // MDXEditor's importer blanks the whole editor on unparseable angle brackets
  // (BIG-4994), so escape them before they ever reach it. The transform is
  // idempotent and renders identically, so round-tripping through save is safe.
  // Memoized so the regex passes only run when `content` changes, not on every
  // focus/blur re-render.
  const safeContent = useMemo(() => escapeUnparseableAngleBrackets(content || ""), [content]);
  const [markdown, setMarkdown] = useState(safeContent);
  // Set when MDXEditor's importer rejects a construct it has no plugin for.
  // Lexical swallows that throw partway through its update, so the editor is
  // left holding a document truncated at the offending node — and `saveOnBlur`
  // would happily persist that truncation over the real body. Falling back to
  // the read-only renderer shows the whole document and takes saving off the
  // table (BIG-5884).
  const [importFailed, setImportFailed] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  // MDXEditor reads `markdown` only on mount — subsequent updates to the
  // prop are ignored. We use this ref to push new content imperatively
  // when the parent's `content` prop changes (e.g. after a Reset, Discard,
  // or navigation between routines).
  const mdxRef = useRef<MDXEditorMethods>(null);

  useEffect(() => {
    if (isFocused && !hasBeenFocused) {
      setHasBeenFocused(true);
    }
  }, [isFocused, hasBeenFocused]);

  // Sync internal state when content prop changes externally (discard,
  // reset, navigation between routines, etc.). MDXEditor reads `markdown`
  // only on mount — re-rendering with a new prop value is ignored — so
  // we push the value through the imperative ref to actually replace
  // what's in the editor.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only reacting to content prop changes
  useEffect(() => {
    if (!isFocused && safeContent !== markdown) {
      setMarkdown(safeContent);
      mdxRef.current?.setMarkdown(safeContent);
      setIsDirty(false);
    }
    // A new body deserves a fresh attempt at the editor — the construct that
    // defeated the importer belonged to the old one.
    setImportFailed(false);
  }, [content]);

  // MDXEditor reports import failures from inside its own render, so the state
  // update is deferred to avoid updating this component while that one renders.
  const handleImportError = ({ error, source }: { error: string; source: string }) => {
    console.error("MDXEditor could not import artifact body:", error, source);
    queueMicrotask(() => setImportFailed(true));
  };

  const handleChange = (newMarkdown: string) => {
    setMarkdown(newMarkdown);
    // Only propagate onChange and mark dirty after the editor has been focused.
    // MDXEditor emits a normalized version of `content` on mount (e.g. whitespace
    // canonicalization); propagating that pre-focus makes parents think the form
    // is dirty when the user hasn't touched anything (BIG-4107).
    if (hasBeenFocused) {
      onChange?.(newMarkdown);
      if (newMarkdown !== safeContent) {
        setIsDirty(true);
      }
    }
  };

  const saveOnBlur = async (e: FocusEvent) => {
    // Focus moving into the editor's own popups (e.g. the link dialog) is not a
    // real blur. Bailing here avoids saving the pre-link content and resetting
    // `hasBeenFocused`/`isDirty`, which would otherwise cause the link the user is
    // inserting to be silently dropped (BIG-5406).
    if (focusMovedToEditorPopup(e.relatedTarget)) return;
    if (isDirty && hasBeenFocused) {
      try {
        await handleSave(markdown);
        setHasBeenFocused(false);
        setIsDirty(false);
      } catch (error) {
        console.error(error);
        errorToast("Error saving");
      }
    }
  };

  // Handle focus detection via event listeners on the editor container
  useEffect(() => {
    const editorElement = editorRef.current;
    if (!editorElement) return;

    const handleFocusIn = () => setIsFocused(true);
    const handleFocusOut = (e: FocusEvent) => {
      // Keep treating the editor as focused when focus moves into one of its own
      // popups (e.g. the link dialog). Without this the toolbar hides the moment
      // the dialog opens.
      if (focusMovedToEditorPopup(e.relatedTarget)) return;
      setIsFocused(false);
    };

    // Use focusin/focusout events which bubble up from child elements
    editorElement.addEventListener("focusin", handleFocusIn);
    editorElement.addEventListener("focusout", handleFocusOut);

    return () => {
      editorElement.removeEventListener("focusin", handleFocusIn);
      editorElement.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  // Memoized on the map: MDXEditor republishes this handler on every render, and
  // each image re-resolves whenever its identity changes, so a fresh closure
  // would refetch every picture on each focus and blur.
  const imagePreviewHandler = useCallback(
    (src: string) => resolveEditorImageSrc(src, imageSrcs),
    [imageSrcs],
  );

  const plugins = useMemo(() => {
    const basePlugins = [
      headingsPlugin(),
      quotePlugin(),
      thematicBreakPlugin(),
      listsPlugin(),
      linkPlugin(),
      // Required for the `CreateLink` toolbar button and for click-to-open/edit
      // on existing links — without it the hyperlink button is a no-op (BIG-5406).
      linkDialogPlugin(),
      // Internal image tokens (`urn:bb:file:…`, `/browser-frames/…`) are the
      // agent's grammar, not URLs; without the handler the editor would request
      // them against the SPA origin and paint a broken picture (BIG-8463).
      imagePlugin({
        imagePreviewHandler,
        ImageDialog: ArtifactImageDialog,
      }),
      codeBlockPlugin({
        defaultCodeBlockLanguage: "js",
        codeBlockEditorDescriptors: [staticCodeBlockDescriptor],
      }),
      // Registers the GFM table syntax extension, without which a table isn't
      // merely unstyled — the pipes never parse as a table at all and the body
      // renders them as literal `| a | b |` text (BIG-5884).
      tablePlugin(),
    ];

    if (readOnly) {
      return basePlugins;
    }

    if (!hideToolbar) {
      return [
        ...basePlugins,
        toolbarPlugin({
          toolbarClassName: cn(
            "artifact-editor-toolbar",
            !isFocused && "artifact-editor-toolbar-hidden",
          ),
          toolbarPosition: "bottom",
          toolbarContents: () => (
            <>
              <UndoRedo />
              <BoldItalicUnderlineToggles />
              <ListsToggle />
              <CreateLink />
            </>
          ),
        }),
      ];
    }

    return basePlugins;
  }, [readOnly, hideToolbar, isFocused, imagePreviewHandler]);

  // Render the original `content`, not `markdown` — `markdown` is whatever
  // MDXEditor managed to import before it gave up, which is the truncated
  // version we're recovering from.
  if (importFailed) {
    return (
      <Markdown
        className="prose prose-zinc max-w-full !text-sm @md:!text-base text-foreground"
        imageSrcs={imageSrcs}
        shouldRenderHtmlImages
      >
        {content}
      </Markdown>
    );
  }

  return (
    <ErrorBoundary
      onError={(err) => {
        console.error("Caught error trying to render markdown:", markdown, err);
      }}
      fallbackRender={({ error }) => (
        <>
          <Markdown imageSrcs={imageSrcs} shouldRenderHtmlImages>
            {markdown}
          </Markdown>
          {showDebug && (
            <div className="text-xs text-muted-foreground admin-only bg-red-100 p-4 rounded-lg overflow-x-scroll">
              <div>
                <span className="text-destructive">Error:</span>{" "}
                {error instanceof Error ? error.message : String(error)}
              </div>
              <pre>{markdown}</pre>
            </div>
          )}
        </>
      )}
    >
      <div ref={editorRef}>
        <MDXEditor
          ref={mdxRef}
          markdown={markdown}
          onChange={handleChange}
          onError={handleImportError}
          readOnly={readOnly}
          plugins={plugins}
          onBlur={saveOnBlur}
          className={className}
          contentEditableClassName={cn(
            // `max-w-full` overrides prose's default ~65ch cap so the body wraps
            // to the full artifact width instead of a narrow column.
            "prose prose-zinc max-w-full !text-sm @md:!text-base text-foreground",
            contentEditableClassName,
          )}
        />
      </div>
    </ErrorBoundary>
  );
};
